'use client';

import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import copy from 'copy-to-clipboard';
import { Modal } from '@/components/antd-wrapper/modal/global/modal';
import {
  getOrCreateAccessKey,
  refreshAccessKey,
  AccessKeyInfo,
} from '@/services/access-key';
import { useI18n } from '@/hooks/use-i18n';
import CopyIcon from '/public/copy-1.svg';
import RefreshIcon from '/public/refresh.svg';
import RobotIcon from '/public/robot.svg';
import InfoIcon from '/public/info.svg';
import { IconWrapper } from '@/components/ui/icon-wrapper';
import VerifyIcon from '/public/verify.svg';
import BackIcon from '/public/on-back.svg';

export const OPEN_ACCESS_KEY_MODAL_EVENT = 'openAccessKeyModal';

export function triggerAccessKeyModal() {
  window.dispatchEvent(new Event(OPEN_ACCESS_KEY_MODAL_EVENT));
}

const INSTALL_GUIDE_URL = 'https://clawhub.ai/dmpm-mininglamp/deepminer-skill';

function maskKey(apiKey: string): string {
  if (!apiKey) return apiKey;
  const prefix = 'dm-';
  const body = apiKey.startsWith(prefix) ? apiKey.slice(prefix.length) : apiKey;
  if (body.length <= 8) return apiKey;
  const masked = `${body.slice(0, 4)}${'*'.repeat(12)}${body.slice(-4)}`;
  return apiKey.startsWith(prefix) ? `${prefix}${masked}` : masked;
}

const buildPrompt = (
  apiKey: string,
  t: (key: string, opts?: Record<string, string>) => string,
) =>
  [
    t('components:sidebar.accessKey.promptLine1', { url: INSTALL_GUIDE_URL }),
    t('components:sidebar.accessKey.promptLine2'),
    t('components:sidebar.accessKey.promptAccessKey', { key: apiKey }),
  ].join('\n');

export function AccessKeyModal() {
  const { t } = useI18n(['components', 'common']);
  const [open, setOpen] = useState(false);
  const [keyInfo, setKeyInfo] = useState<AccessKeyInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    const handleOpen = () => setOpen(true);
    window.addEventListener(OPEN_ACCESS_KEY_MODAL_EVENT, handleOpen);
    return () =>
      window.removeEventListener(OPEN_ACCESS_KEY_MODAL_EVENT, handleOpen);
  }, []);

  useEffect(() => {
    if (open) {
      loadKey();
    } else {
      setConfirming(false);
    }
  }, [open]);

  const loadKey = async () => {
    setLoading(true);
    try {
      const data = await getOrCreateAccessKey();
      setKeyInfo(data);
    } catch {
      toast.error(t('components:sidebar.accessKey.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleCopyKey = () => {
    if (!keyInfo?.api_key) return;
    copy(keyInfo.api_key);
    toast.success(t('components:sidebar.accessKey.copied'));
  };

  const handleCopyCommand = () => {
    if (!keyInfo?.api_key) return;
    copy(buildPrompt(keyInfo.api_key, t));
    toast.success(t('components:sidebar.accessKey.commandCopied'));
  };

  const handleOpenDocs = () => {
    window.open(
      'https://mcn1x2r9j128.feishu.cn/wiki/AewwwhBZtiCpiOk18hecbnTenPe',
      '_blank',
    );
  };

  const handleResetClick = () => {
    setConfirming(true);
  };

  const handleConfirmCancel = () => {
    setConfirming(false);
  };

  const handleConfirmOk = async () => {
    setLoading(true);
    try {
      const data = await refreshAccessKey(keyInfo!.id);
      if (data) {
        setKeyInfo(data);
        setConfirming(false);
        toast.success(t('components:sidebar.accessKey.refreshSuccess'));
      }
    } catch {
      toast.error(t('components:sidebar.accessKey.refreshFailed'));
    } finally {
      setLoading(false);
    }
  };

  const maskedKey = loading ? '...' : maskKey(keyInfo?.api_key || '');

  // Prompt 展示：URL 替换为真实文档链接，AccessKey 行用脱敏 key
  const promptLines = keyInfo?.api_key
    ? [
        t('components:sidebar.accessKey.promptLine1', {
          url: INSTALL_GUIDE_URL,
        }),
        t('components:sidebar.accessKey.promptLine2'),
        maskKey(keyInfo.api_key),
      ]
    : [];

  return (
    <>
      <Modal
        open={open}
        onCancel={() => setOpen(false)}
        title={t('components:sidebar.accessKey.title')}
        width={800}
        height={633}
        footer={null}
        closable={true}
        classNames={{
          content: '!pb-[0]',
        }}
      >
        {/* 主视图 */}
        <div className="flex flex-col pt-1 pb-28.5 ">
          {/* 副标题 */}
          <div className="text-[12px] py-[7px] flex items-center justify-center text-[#7171ee] mb-[17px] bg-[#7171EE33]">
            {t('components:sidebar.accessKey.subtitle')}
          </div>

          {/* 底部指令大卡片 */}
          <div className="flex flex-col px-6">
            {/* 卡片头部：icon + 标题 + 复制按钮 */}
            <div className="flex items-center gap-3 ">
              <div className="shrink-0 flex items-center justify-center w-12 h-12 rounded-full bg-[#7171EE]">
                <RobotIcon className="w-8 h-8 text-white" />
              </div>
              <div className="flex flex-col flex-1 min-w-0">
                <span className="text-[18px] font-semibold text-[#000000BF]">
                  {t('components:sidebar.accessKey.stepTitle')}
                </span>
                <span className="text-[14px] text-[#00000073]">
                  {t('components:sidebar.accessKey.stepDesc')}
                </span>
              </div>
              <button
                onClick={handleCopyCommand}
                disabled={loading || !keyInfo?.api_key}
                className="shrink-0 flex items-center justify-center min-w-[100px] h-10 px-6 py-[11px] rounded-lg text-sm font-medium text-white whitespace-nowrap bg-[#7171ee] hover:bg-[#5959d9] transition-colors disabled:opacity-40 cursor-pointer"
              >
                {t('components:sidebar.accessKey.copyCommand')}
              </button>
            </div>

            {/* Prompt 文本区 */}
            <div className=" mt-6 px-6 py-2.5 bg-[#F8F8FA] rounded-lg">
              {promptLines.length > 0 ? (
                <div className="flex flex-col gap-0.5">
                  <p className="text-[14px] leading-[28px] text-[#000000BF] m-0">
                    {promptLines[0]}
                  </p>
                  <p className="text-[14px] leading-[28px] text-[#000000BF] m-0">
                    {promptLines[1]}
                  </p>
                  <p className="text-[14px] leading-[28px] font-mono m-0">
                    <span className="text-[#000000BF]">AccessKey: </span>
                    <span className="text-[#7171EE]">
                      {maskKey(keyInfo?.api_key || '')}
                    </span>
                  </p>
                </div>
              ) : (
                <p className="text-[12px] text-[rgba(0,0,0,0.25)] m-0">...</p>
              )}
            </div>

            {/* 安全提示 */}
            <div className="mt-4 flex items-center gap-1 px-3 py-2 bg-[#f8f8f8] rounded-[8px]">
              <InfoIcon className="w-4 h-4 text-[rgba(0,0,0,0.35)]" />
              <span className="text-[14px] leading-relaxed text-[#000000BF]">
                {t('components:sidebar.accessKey.securityTip')}
              </span>
            </div>
          </div>

          <div className="border-t border-dashed border-[#EBEBEB] my-3 mx-6" />

          {/* 上方两卡片横排 */}
          <div className="flex gap-3 px-6">
            {/* Access Key 卡片 */}
            <div className="flex flex-col flex-1 min-w-0 rounded-lg bg-[#F8F8FA] p-4">
              {/* 标题行 */}
              <div className="flex items-center justify-between mb-2.5">
                <span className="text-[20px] font-semibold text-[#000000BF]">
                  {t('components:sidebar.accessKey.keyCardTitle')}
                </span>
                <span className="inline-flex items-center gap-1 text-[13px] text-[rgba(0,0,0,0.35)]">
                  <VerifyIcon className="w-4  h-4  text-[#c9c9c9]" />
                  {t('components:sidebar.accessKey.spaceOnly')}
                </span>
              </div>

              {/* key 值行 + 复制按钮 */}
              <div className="flex items-center justify-between gap-3 mb-3">
                <span className="truncate font-mono text-[16px] flex-1 min-w-0 text-[rgba(0,0,0,0.75)]">
                  {maskedKey}
                </span>
                <button
                  onClick={handleCopyKey}
                  disabled={loading || !keyInfo?.api_key}
                  className="shrink-0 inline-flex items-center gap-1 text-[12px] h-[26px] px-2 border border-[#E5E5E5] rounded-[6px] bg-white text-[rgba(0,0,0,0.75)] transition-colors cursor-pointer disabled:opacity-40 hover:bg-[rgba(0,0,0,0.04)]"
                >
                  <IconWrapper
                    icon={CopyIcon}
                    size={12}
                    className="text-[rgba(0,0,0,0.75)]"
                  />
                  {t('common:copy')}
                </button>
              </div>

              {/* 重置按钮 */}
              <button
                onClick={handleResetClick}
                disabled={loading}
                className="self-start inline-flex items-center gap-1.5 text-[12px] h-[26px] px-2 border border-[#E5E5E5] rounded-[6px] bg-white text-[rgba(0,0,0,0.75)] transition-colors cursor-pointer disabled:opacity-40 hover:bg-[rgba(0,0,0,0.04)]"
              >
                <IconWrapper
                  icon={RefreshIcon}
                  size={13}
                  className="text-[rgba(0,0,0,0.75)]"
                />
                {t('components:sidebar.accessKey.reset')}
                {t('components:sidebar.accessKey.keyCardTitle')}
              </button>
            </div>

            {/* 文档卡片 */}
            <div className="flex flex-col flex-1 min-w-0 justify-center rounded-lg bg-[#F8F8FA] px-8 py-10">
              <p className="text-[20px] font-semibold text-[rgba(0,0,0,0.85)] mb-2">
                {t('components:sidebar.accessKey.docsCardDesc')}
              </p>
              <button
                onClick={handleOpenDocs}
                className="self-start inline-flex items-center gap-1 text-[15px] text-[#7171EE] hover:text-[#5959d9] transition-colors cursor-pointer"
              >
                {t('components:sidebar.accessKey.docsCardTitle')}
                <BackIcon className="w-4  h-4  rotate-180 text-[#7171ee]" />
              </button>
            </div>
          </div>
        </div>
      </Modal>
      {/* 二次确认弹窗 */}
      <Modal
        open={confirming}
        onCancel={handleConfirmCancel}
        title={t('components:sidebar.accessKey.confirmTitle')}
        width={400}
        footer={null}
        closable={true}
        classNames={{
          content: '!pb-[0]',
        }}
      >
        <div className="flex flex-col px-6 pt-2">
          <p className="text-[14px] font-normal leading-relaxed text-[#000000BF]">
            {t('components:sidebar.accessKey.confirmContent')}
          </p>
          <div className="flex items-center justify-end gap-4 mt-[56px]">
            <button
              onClick={handleConfirmCancel}
              disabled={loading}
              className="w-20 h-10 rounded-lg text-sm font-normal text-[#7171EE] bg-[#7171EE1A] hover:bg-[#7171EE33] transition-colors cursor-pointer disabled:opacity-50"
            >
              {t('common:cancel')}
            </button>
            <button
              onClick={handleConfirmOk}
              disabled={loading}
              className="w-20 h-10 rounded-lg text-sm font-medium text-white bg-[#7171ee] hover:bg-[#5959d9] transition-colors disabled:opacity-50 cursor-pointer"
            >
              {t('common:confirm')}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}
